// src/pages/Privacy.js
import React from "react";
import { Box, Container, Paper, Typography } from "@mui/material";
import Seo from "../components/Seo";
import { SITE } from "../data/site";
import { buildBreadcrumbSchema, buildWebPageSchema } from "../utils/schema";
import "./Privacy.css";

export default function Privacy() {
  const title = "Privacy Policy — Brainwave Solutions";
  const description = "Privacy policy for Brainwave Solutions, including analytics and contact form handling.";
  const canonical = "https://brainwavesolutions.net/privacy";
  const structuredData = [
    buildWebPageSchema({ title, description, canonical }),
    buildBreadcrumbSchema([
      { name: "Home", url: SITE.url },
      { name: "Privacy Policy", url: canonical },
    ]),
  ];

  return (
    <>
      <Seo
        title={title}
        description={description}
        canonical={canonical}
        structuredData={structuredData}
      />

      <Box className="bws-privacy">
        <Container maxWidth="md">
          <Typography variant="h1" className="bws-privacyTitle">
            Privacy Policy
          </Typography>

          <Typography className="bws-privacyLead">
            This Privacy Policy explains how Brainwave Solutions (“we,” “us,” or “our”) collects, uses, and shares
            information when you visit brainwavesolutions.net (the “Site”) or contact us.
          </Typography>

          <Paper elevation={0} className="bws-privacyCard">
            <Typography variant="h3" className="bws-privacyH2">Information we collect</Typography>

            <Typography className="bws-privacyP">
              <strong>Information you provide:</strong> If you contact us (for example, via our contact form), you may
              provide information such as your name, email address, and message content.
            </Typography>

            <Typography className="bws-privacyP">
              <strong>Information collected automatically:</strong> When you visit the Site, we may collect limited
              technical information such as pages viewed, approximate location (derived from IP), device/browser
              information, and general usage patterns.
            </Typography>

            <Typography variant="h3" className="bws-privacyH2">How we use information</Typography>
            <ul className="bws-privacyList">
              <li>To respond to inquiries and provide services</li>
              <li>To operate, maintain, and improve the Site</li>
              <li>To understand how visitors use the Site (analytics)</li>
              <li>To protect against abuse, fraud, or security incidents</li>
            </ul>

            <Typography variant="h3" className="bws-privacyH2">Analytics and cookies</Typography>
            <Typography className="bws-privacyP">
              We use Umami to understand Site usage and improve the user experience.
            </Typography>
            <Typography className="bws-privacyP">
              We use Google Analytics to understand Site usage and improve the user experience. Google Analytics may use
              cookies and similar technologies to collect usage information.
            </Typography>
            <Typography className="bws-privacyP">
              Where required by law, cookies are used only after you consent. You can change your preference by
              declining cookies when prompted.
            </Typography>

            <Typography variant="h3" className="bws-privacyH2">Sharing of information</Typography>
            <Typography className="bws-privacyP">
              We do not sell your personal information. We may share information with service providers that help us run
              the Site and communicate with you (such as analytics and contact form processing), or if required by law.
            </Typography>

            <Typography variant="h3" className="bws-privacyH2">Data retention</Typography>
            <Typography className="bws-privacyP">
              We retain contact messages as needed to respond and for business recordkeeping. Analytics data retention is
              controlled by our analytics settings.
            </Typography>

            <Typography variant="h3" className="bws-privacyH2">Your choices</Typography>
            <ul className="bws-privacyList">
              <li>You can choose whether to allow analytics cookies (where prompted).</li>
              <li>
                You can disable cookies in your browser settings, though the Site may not function as intended in some
                cases.
              </li>
              <li>
                You can contact us to request access, correction, or deletion of information you’ve submitted to us,
                subject to applicable law.
              </li>
            </ul>

            <Typography variant="h3" className="bws-privacyH2">Security</Typography>
            <Typography className="bws-privacyP">
              We take reasonable measures to protect information, but no method of transmission or storage is 100% secure.
            </Typography>

            <Typography variant="h3" className="bws-privacyH2">Contact</Typography>
            <Typography className="bws-privacyP">
              If you have questions about this Privacy Policy, contact:{" "}
              <a className="bws-privacyLink" href="mailto:nate.parish@brainwavesolutions.net">
                nate.parish@brainwavesolutions.net
              </a>
            </Typography>
          </Paper>
        </Container>
      </Box>
    </>
  );
}
